import { useGame } from "../store.js";

interface VerifierOverlayProps {
  /** Opens the existing fairness modal scoped to the most-recently-resolved
   *  round id. Caller wires this up so the overlay reuses the modal flow.    */
  onVerify: (roundId: string | null) => void;
  /** The id we should pass back when the user clicks Verify — typically the
   *  last resolved round (since the live round's seed is still secret).      */
  lastResolvedRoundId: string | null;
}

const wrap: React.CSSProperties = {
  position: "fixed",
  bottom: 16,
  right: 16,
  zIndex: 60,
  width: 280,
  background: "linear-gradient(180deg, rgba(14,18,26,0.96), rgba(8,10,14,0.96))",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  padding: "10px 12px",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--fg-2)",
  backdropFilter: "blur(6px)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--font-ui)",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--fg-3)",
  marginRight: 4,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 8,
  marginTop: 4,
  whiteSpace: "nowrap",
};

const valStyle: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: "var(--fg-1)",
};

/**
 * Sticky corner panel that surfaces the live session's commitment data —
 * server-seed hash, client seed, and the round nonce. Streamer-friendly:
 * viewers can spot-check the same fields the verifier modal shows in detail.
 *
 * Toggle this on via the "Show fairness data" setting; the data is derived
 * straight from the store fields hydrated at session launch + on every
 * round open.
 */
export function VerifierOverlay({ onVerify, lastResolvedRoundId }: VerifierOverlayProps) {
  const serverSeedHash = useGame((s) => s.serverSeedHash);
  const clientSeed = useGame((s) => s.clientSeed);
  const nonce = useGame((s) => s.roundNonce);
  const setFairnessOverlay = useGame((s) => s.setFairnessOverlay);

  if (!serverSeedHash) return null;

  const hashShort = `${serverSeedHash.slice(0, 8)}…${serverSeedHash.slice(-6)}`;

  return (
    <div style={wrap} role="region" aria-label="Live fairness commitment">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--cyan)",
          }}
        >
          Provably-Fair · Live
        </span>
        <button
          type="button"
          onClick={() => setFairnessOverlay(false)}
          aria-label="Hide fairness overlay"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--fg-3)",
            fontSize: 14,
            cursor: "pointer",
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Server hash</span>
        <span style={valStyle} title={serverSeedHash}>
          {hashShort}
        </span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Client seed</span>
        <span style={valStyle} title={clientSeed ?? ""}>
          {clientSeed ?? "—"}
        </span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Nonce</span>
        <span style={valStyle}>{nonce ?? 0}</span>
      </div>

      <button
        type="button"
        onClick={() => onVerify(lastResolvedRoundId)}
        disabled={!lastResolvedRoundId}
        style={{
          marginTop: 10,
          width: "100%",
          padding: "6px 10px",
          background: lastResolvedRoundId ? "var(--cyan)" : "var(--line)",
          color: lastResolvedRoundId ? "#0A0E14" : "var(--fg-3)",
          border: "none",
          borderRadius: 4,
          fontFamily: "var(--font-ui)",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          cursor: lastResolvedRoundId ? "pointer" : "not-allowed",
        }}
      >
        Verify last round
      </button>
    </div>
  );
}
